import React from 'react';
import uuid from 'uuid';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { parse } from 'query-string';
import { compose, lifecycle, withHandlers, withPropsOnChange } from 'recompose';
import { ObjectShim } from '@packages/helpers/core/shims/object-shim';
import { CoachMessage } from '../components/coach-message';
import { Form } from '../components/inputs/input-group';
import { Column } from '../components/layout/grid';
import { Footer } from '../components/layout/page-footer';
import { Header } from '../components/layout/page-header';
import { PurifiedHtmlContainer } from '../components/layout/purified-html-container';
import { Spacer } from '../components/layout/spacers';
import { MetaTags } from '../components/meta-tags';
import { TBody, TComponent, Template, THeadline } from '../components/template';
import styles from '../styles/templates/account-page/account-page-template.module.scss';
import { resetDisableNavigation, setDisableNavigation } from '../store/reducers/navigation';

const SEO_PAGE_DATA_NAME = 'default';

const AccountPageComponent = React.memo(
  ({ heading, body, inPageMessage, message, children, modal, onSubmit, pageHeader }) => {
    return (
      <>
        <MetaTags name={SEO_PAGE_DATA_NAME} />
        {pageHeader?.heading && (
          <Header
            heading={pageHeader?.heading}
            subheading={pageHeader?.subheading}
            category={pageHeader?.category}
            image={pageHeader?.image}
            type={pageHeader?.type}
            backgroundPosition={pageHeader?.backgroundPosition}
          />
        )}
        <Template>
          <Spacer type='top' />
          {inPageMessage && (
            <>
              <Column className={styles['in-page-message']}>
                <p>{inPageMessage}</p>
              </Column>
              <Spacer type='component' />
            </>
          )}
          {heading && (
            <Column>
              <THeadline title={heading} />
            </Column>
          )}
          {body && (
            <>
              <TBody wrapperAs={TComponent} html={body} />
              <Spacer type='component' />
            </>
          )}
          {message && (
            <>
              <Column>
                <CoachMessage>
                  {[].concat(message).map(line => (
                    <PurifiedHtmlContainer
                      key={uuid('account-page-message')}
                      renderAs='p'
                      className='typography'
                      html={line}
                    />
                  ))}
                </CoachMessage>
              </Column>
              <Spacer type='component' />
            </>
          )}
          <Column>
            <Form onSubmit={onSubmit}>{children}</Form>
          </Column>
          <Spacer type='bottom' />
        </Template>
        <Footer pageType='account' />
        {modal}
      </>
    );
  }
);

// Is needed as a part of unification of pageHeader configuration to support previous and new header configuration approaches for account pages
export const AccountPage = compose(
  connect(null, { setDisableNavigation, resetDisableNavigation }),
  withPropsOnChange(
    ['title', 'subheading', 'category', 'headerImage', 'pageHeader'],
    ({ title, subheading, category, headerImage, pageHeader }) => ({
      pageHeader: ObjectShim.mergeDeep(
        { heading: title, subheading: subheading, category: category, image: headerImage },
        pageHeader
      )
    })
  ),
  withRouter,
  withHandlers({
    checkDisableNavigation:
      ({ location, setDisableNavigation }) =>
      () => {
        const { search } = location;
        const disableNavigation = parse(search)?.disableNavigation;

        setDisableNavigation({ disableNavigation });
      }
  }),
  lifecycle({
    componentDidMount() {
      const { checkDisableNavigation } = this.props;

      checkDisableNavigation();
    },
    componentDidUpdate(prevProps) {
      const { checkDisableNavigation, location } = this.props;
      if (prevProps.location.pathname !== location.pathname) {
        checkDisableNavigation();
      }
    },
    componentWillUnmount() {
      const { resetDisableNavigation } = this.props;
      resetDisableNavigation();
    }
  })
)(AccountPageComponent);
